import { useState, useEffect, useRef, useCallback } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  const openItem = async (n) => {
    try { await api.patch(`/notifications/${n.id}/read`); } catch {}
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => { try { await api.patch("/notifications/read-all"); } catch {} load(); };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative grid h-9 w-9 place-items-center border border-gray-200 hover:border-[#0a0a0a] transition-colors"
      >
        <Bell className="h-4 w-4 text-[#0a0a0a]" />
        {unread > 0 && (
          <span data-testid="notification-count" className="absolute -top-1.5 -right-1.5 grid h-4 min-w-4 px-1 place-items-center rounded-full bg-orange-500 text-white text-[10px] font-bold">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 shadow-lg z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-500">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-[11px] text-orange-600 hover:underline" data-testid="notification-mark-all">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-auto">
            {items.length === 0 && <div className="px-4 py-8 text-center text-sm text-gray-400">No notifications</div>}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${n.read ? "" : "bg-orange-50/40"}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-orange-500 shrink-0" />}
                  <div className={n.read ? "pl-3.5" : ""}>
                    <div className="text-sm font-semibold text-[#0a0a0a]">{n.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{n.body}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
